'use client';

import { useEffect, useRef } from 'react';

interface UseKeyboardNavigationOptions {
  enabled?: boolean;
  orientation?: 'horizontal' | 'vertical';
  wrap?: boolean;
  onActivate?: (element: HTMLElement) => void;
}

export function useKeyboardNavigation({
  enabled = true,
  orientation = 'horizontal',
  wrap = true,
  onActivate
}: UseKeyboardNavigationOptions = {}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!enabled) return;

    const container = containerRef.current;
    if (!container) return;

    const getFocusableElements = () => {
      const focusableSelectors = [
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'a[href]',
        '[tabindex="0"]',
        '[role="tab"]:not([aria-disabled="true"])',
        '[role="button"]:not([aria-disabled="true"])',
        '[role="menuitem"]:not([aria-disabled="true"])'
      ].join(', ');

      return Array.from(container.querySelectorAll(focusableSelectors)) as HTMLElement[];
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      const elements = getFocusableElements();
      if (elements.length === 0) return;

      const currentIndex = elements.findIndex(el => el === document.activeElement);
      if (currentIndex === -1) return;

      let nextIndex = currentIndex;
      let handled = false;

      const isHorizontal = orientation === 'horizontal';
      const nextKey = isHorizontal ? 'ArrowRight' : 'ArrowDown';
      const prevKey = isHorizontal ? 'ArrowLeft' : 'ArrowUp';

      switch (e.key) {
        case nextKey:
          nextIndex = currentIndex + 1;
          if (nextIndex >= elements.length) {
            nextIndex = wrap ? 0 : elements.length - 1;
          }
          handled = true;
          break;

        case prevKey:
          nextIndex = currentIndex - 1;
          if (nextIndex < 0) {
            nextIndex = wrap ? elements.length - 1 : 0;
          }
          handled = true;
          break;

        case 'Home':
          nextIndex = 0;
          handled = true;
          break;

        case 'End':
          nextIndex = elements.length - 1;
          handled = true;
          break;

        case 'Enter':
        case ' ':
          if (onActivate && elements[currentIndex]) {
            e.preventDefault();
            onActivate(elements[currentIndex]);
            handled = true;
          }
          break;
      }

      if (handled && nextIndex !== currentIndex) {
        e.preventDefault();
        elements[nextIndex]?.focus();
      }
    };

    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, orientation, wrap, onActivate]);

  return containerRef;
}